import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, resolveDynamicComponent as _resolveDynamicComponent, openBlock as _openBlock, createBlock as _createBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode } from "vue"

const _hoisted_1 = {
  key: 0,
  class: "promopage"
}

import { StrapiTemplatesEnum } from '@/types/strapiTemplateEnum';
import {
  computed,
  defineAsyncComponent,
  onMounted,
  reactive,
  ref,
  watch,
} from '@vue/runtime-core';
import { useRoute } from 'vue-router';
import { useStore } from 'vuex';
import { PromoPageInterface } from '~/types/promoPage.interface';
import Bugsnag from '@bugsnag/js';


export default /*#__PURE__*/_defineComponent({
  setup(__props) {

const route = useRoute();

const store = useStore();

const isReady = ref(false);

const pageData = computed(
  (): PromoPageInterface[] => store.getters['promoPage/pageData']
);
const isPromoPageLoaded = computed(
  (): boolean => store.getters['promoPage/isLoaded']
);
const userCountry = computed(() => store.getters['country/country']);

const titleData: {
  titles: string[];
  index: number;
} = reactive({
  titles: [],
  index: 0,
});

/**
 * Method for transforming country/stag params to array
 *
 * @returns true - if finded
 * @returns false - if not finded
 * @returns null - if string is not defined
 */
const parseAndFind = (
  value: string | null | undefined,
  search?: string | number
): boolean | null => {
  if (!value) {
    return null;
  }

  return value
    .split(',')
    .map((v) => v.toUpperCase())
    .includes(String(search));
};

/**
 * Get Stag from url
 */
const stag = route.query?.stag ? String(route.query?.stag) : '';

/**
 * Get promo pages, filtered by included/excluded countries and stag`s
 */
const filteredPages = computed(() => {
  return pageData.value.filter(
    (p) =>
      !(
        parseAndFind(p.show_rules.included_countries, userCountry.value) ===
          false ||
        parseAndFind(p.show_rules.excluded_countries, userCountry.value) ===
          true ||
        parseAndFind(p.show_rules.included_stags, stag) === false ||
        parseAndFind(p.show_rules.excluded_stags, stag) === true
      )
  );
});

/**
 * Get current pages, with max specificity by stag and country
 */
const currentPage = computed((): PromoPageInterface | null => {
  return (
    filteredPages.value.find(
      (p) =>
        parseAndFind(p.show_rules.included_countries, userCountry.value) &&
        parseAndFind(p.show_rules.included_stags, stag)
    ) ||
    filteredPages.value.find(
      (p) =>
        !parseAndFind(p.show_rules.included_countries, userCountry.value) &&
        parseAndFind(p.show_rules.included_stags, stag)
    ) ||
    filteredPages.value.find(
      (p) =>
        parseAndFind(p.show_rules.included_countries, userCountry.value) &&
        !parseAndFind(p.show_rules.included_stags, stag)
    ) ||
    filteredPages.value.find(
      (p) =>
        !parseAndFind(p.show_rules.included_countries, userCountry.value) &&
        !parseAndFind(p.show_rules.included_stags, stag)
    ) ||
    null
  );
});

let intervalId: number;

const startTitleBleems = (): void => {
  if (intervalId) {
    window.clearInterval(intervalId);
  }

  if (
    currentPage.value?.head?.title &&
    currentPage.value?.head?.title.split('|').length > 1
  ) {
    titleData.titles = currentPage.value?.head?.title.split('|') || [''];
    titleData.index = 0;
  } else {
    document.title = currentPage.value?.head.title || '';
  }

  intervalId = window.setInterval(() => {
    if (titleData.index >= titleData.titles.length - 1) {
      titleData.index = 0;
    } else {
      titleData.index++;
    }
  }, 1000);
};

watch(
  () => titleData.index,
  (i) => {
    document.title = titleData.titles[i];
  }
);

const changeFavicon = (url: string): void => {
  let link: HTMLLinkElement = document.head.querySelector("link[rel~='icon']");
  if (!link) {
    link = document.createElement('link');
    link.rel = 'icon';
    document.getElementsByTagName('head')[0].appendChild(link);
  }
  link.href = url;
};

const setFavicon = (): void => {
  if (currentPage.value?.params[0].favicon) {
    changeFavicon(currentPage.value.params[0].favicon.url);
  } else {
    changeFavicon('/favicon-7bit.ico');
  }
};

const setDescription = (): void => {
  document.getElementsByTagName('meta')['description'].content =
    currentPage.value?.head.meta_description || '';
};

const promoComponent = computed(() => {
  if (isPromoPageLoaded.value && currentPage.value?.params?.[0]?.__component) {
    const template =
      StrapiTemplatesEnum[currentPage.value.params[0].__component];
    return defineAsyncComponent(
      () => import(`@/components/promoPage/${template}.vue`)
    );
  } else {
    return null;
  }
});

const isPlayerAuthorised = computed(
  (): boolean => store.getters['ssPlayer/isPlayerAuthorised']
);

const redirectHost = computed(() => store.getters['systemHosts/redirectHost']);

onMounted(async () => {
  await store.dispatch('systemHosts/load');
  await Promise.all([
    store.dispatch('autochangeData/load'),
    store.dispatch('country/check'),
    store.dispatch('promoPage/load', { path: route.params.path }),
    store.dispatch('ssPlayer/loadPlayerInfo'),
  ]);

  // for landing template modern australia katsubet if player authorize then redirect to main site. For modern2 another logic
  if (
    isPlayerAuthorised.value &&
    StrapiTemplatesEnum[currentPage.value.params[0].__component] !==
      'modern2Promo'
  ) {
    window.location.href = `https://${redirectHost.value}`;
    return;
  }

  const lang = currentPage.value?.lang || 'en';
  store.dispatch('currencyMenu/load');
  store.dispatch('regForm/load', lang);
  store.dispatch('stag/checkStag', lang);

  isReady.value = true;
  startTitleBleems();
  setFavicon();
  setDescription();

  if (!currentPage.value?.params?.[0]?.__component) {
    Bugsnag.notify(`No such promo page [ ${route.params.path} ] !`);
  }
});

return (_ctx: any,_cache: any) => {
  return (isReady.value && _unref(promoComponent))
    ? (_openBlock(), _createElementBlock("div", _hoisted_1, [
        (_openBlock(), _createBlock(_resolveDynamicComponent(_unref(promoComponent)), { "page-data": _unref(currentPage) }, null, 8, ["page-data"]))
      ]))
    : _createCommentVNode("", true)
}
}

})